import { Sparkles, CheckCircle2, AlertTriangle, XCircle, Loader2, RotateCcw } from "lucide-react";

import { formatAmount } from "@/utils/formatAmount";
import type { ClientInvoiceAiAnalysis, ClientInvoiceAiLineItem } from "@/features/finances/types";

/**
 * Wynik analizy AI pliku faktury PRZ — sprzedawca, nabywca, netto, termin płatności
 * i podział na pozycje. Backend dosyła `ai_analysis` tym, którzy mają dostęp do finansów
 * tej faktury (Prezes, księgowość, dyrektor/koordynator z grantem na projekt).
 */

const STYLES: Record<
  ClientInvoiceAiAnalysis["status"],
  { Icon: typeof Sparkles; cls: string; spin?: boolean }
> = {
  ok: { Icon: CheckCircle2, cls: "text-emerald-600 dark:text-emerald-400" },
  warning: { Icon: AlertTriangle, cls: "text-amber-600 dark:text-amber-400" },
  error: { Icon: XCircle, cls: "text-red-600 dark:text-red-400" },
  pending: { Icon: Loader2, cls: "text-gray-400", spin: true },
  running: { Icon: Loader2, cls: "text-blue-500", spin: true },
};

function Row({
  label,
  ok,
  expected,
  found,
}: {
  label: string;
  ok?: boolean | null;
  expected?: string | null;
  found?: string | null;
}) {
  const { Icon, cls } = ok === true ? STYLES.ok : ok === false ? STYLES.error : STYLES.warning;
  return (
    <li className="flex items-start gap-2 py-1.5">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cls}`} />
      <div className="min-w-0">
        <span className="font-medium text-gray-800 dark:text-gray-200">{label}</span>
        {(expected || found) && (
          <div className="text-xs text-gray-500 dark:text-gray-400 [overflow-wrap:anywhere]">
            {expected && (
              <>
                w systemie: <span className="font-mono">{expected}</span>
              </>
            )}
            {expected && found && " · "}
            {found && (
              <>
                na fakturze: <span className="font-mono">{found}</span>
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function itemLabel(it: ClientInvoiceAiLineItem): string {
  const qty = it.quantity && it.unit_value ? `${it.quantity} × ${formatAmount(it.unit_value)}` : "";
  return [it.name || "—", qty].filter(Boolean).join(" · ");
}

function LineItemsTable({
  title,
  items,
}: {
  title: string;
  items: ClientInvoiceAiLineItem[];
}) {
  if (items.length === 0) return null;
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{title}</p>
      <ul className="space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-gray-700 dark:text-gray-300 flex justify-between gap-2">
            <span className="min-w-0 [overflow-wrap:anywhere]">{itemLabel(it)}</span>
            <span className="font-mono shrink-0">{it.total ? formatAmount(it.total) : "—"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ClientInvoiceAiPanel({
  analysis,
  hasFile,
  onRerun,
  rerunning,
}: {
  analysis?: ClientInvoiceAiAnalysis | null;
  /** Bez pliku nie ma czego analizować — przycisk zostaje zablokowany. */
  hasFile: boolean;
  /** Podane, gdy oglądający może uruchomić analizę (backend i tak sprawdza dostęp). */
  onRerun?: () => void;
  rerunning?: boolean;
}) {
  // Ani wyniku, ani prawa do uruchomienia — dla tego użytkownika sekcja nie istnieje.
  if (!analysis && !onRerun) return null;

  const runButton = onRerun && (
    <button
      type="button"
      onClick={onRerun}
      disabled={!hasFile || rerunning}
      title={hasFile ? undefined : "Najpierw wgraj plik faktury"}
      className={`text-xs px-2 py-1 rounded border inline-flex items-center gap-1 ${
        hasFile && !rerunning
          ? "border-purple-300 text-purple-700 hover:bg-purple-100 dark:text-purple-300 dark:border-purple-800"
          : "border-gray-200 text-gray-400 cursor-not-allowed dark:border-gray-700"
      }`}
    >
      <RotateCcw className={`w-3 h-3 ${rerunning ? "animate-spin" : ""}`} />
      {analysis ? "Analizuj ponownie" : "Uruchom analizę"}
    </button>
  );

  if (!analysis) {
    return (
      <div className="rounded-lg border border-purple-200 dark:border-purple-900/50 bg-purple-50/40 dark:bg-purple-900/10 p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span className="font-semibold text-gray-900 dark:text-gray-100">Analiza AI faktury</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {hasFile ? "Jeszcze nieprzeprowadzona." : "Brak pliku faktury."}
          </span>
          <span className="ml-auto">{runButton}</span>
        </div>
      </div>
    );
  }

  const { Icon, cls, spin } = STYLES[analysis.status] ?? STYLES.warning;
  const busy = analysis.status === "pending" || analysis.status === "running";
  const expectedItems = analysis.expected_line_items ?? [];
  const foundItems = analysis.line_items_found ?? [];

  return (
    <div className="rounded-lg border border-purple-200 dark:border-purple-900/50 bg-purple-50/40 dark:bg-purple-900/10 p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-purple-500" />
        <span className="font-semibold text-gray-900 dark:text-gray-100">Analiza AI faktury</span>
        <Icon className={`w-4 h-4 ${cls} ${spin ? "animate-spin" : ""}`} />
        <span className={`text-sm ${cls}`}>{analysis.status_label}</span>
        <span className="ml-auto">{runButton}</span>
      </div>
      {busy ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Analiza uruchamia się automatycznie po wgraniu pliku faktury…
        </p>
      ) : analysis.status === "error" ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          {analysis.error_message || analysis.summary || "Nie udało się przeanalizować dokumentu."}
        </p>
      ) : (
        <>
          {analysis.summary && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{analysis.summary}</p>
          )}
          <ul className="divide-y divide-purple-100 dark:divide-purple-900/40">
            <Row
              label="Sprzedawca (spółka GK)"
              ok={analysis.seller_ok}
              expected={analysis.expected_seller}
              found={analysis.seller_found}
            />
            <Row
              label="Nabywca (klient)"
              ok={analysis.buyer_ok}
              expected={
                analysis.expected_buyer
                  ? analysis.expected_buyer +
                    (analysis.expected_buyer_nip ? ` (NIP ${analysis.expected_buyer_nip})` : "")
                  : undefined
              }
              found={
                analysis.buyer_found
                  ? analysis.buyer_found +
                    (analysis.buyer_nip_found ? ` (NIP ${analysis.buyer_nip_found})` : "")
                  : undefined
              }
            />
            <Row
              label="Kwota netto"
              ok={analysis.net_amount_ok}
              expected={analysis.expected_net ? formatAmount(analysis.expected_net) : undefined}
              found={analysis.net_amount_found ? formatAmount(analysis.net_amount_found) : undefined}
            />
            <Row
              label="Termin płatności"
              ok={analysis.payment_term_ok}
              expected={
                analysis.expected_payment_term_days != null
                  ? `${analysis.expected_payment_term_days} dni`
                  : undefined
              }
              found={
                analysis.payment_term_days_found != null
                  ? `${analysis.payment_term_days_found} dni`
                  : undefined
              }
            />
            <Row
              label="Podział na pozycje"
              ok={analysis.line_items_ok}
              expected={expectedItems.length ? `${expectedItems.length} poz.` : undefined}
              found={
                foundItems.length
                  ? `${foundItems.length} poz.` +
                    (analysis.line_items_total_found
                      ? ` / ${formatAmount(analysis.line_items_total_found)}`
                      : "")
                  : undefined
              }
            />
          </ul>
          {analysis.line_items_note && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">{analysis.line_items_note}</p>
          )}
          {(expectedItems.length > 0 || foundItems.length > 0) && (
            <div className="grid gap-4 sm:grid-cols-2 mt-3 pt-3 border-t border-purple-100 dark:border-purple-900/40">
              <LineItemsTable title="Pozycje w systemie" items={expectedItems} />
              <LineItemsTable title="Pozycje na dokumencie" items={foundItems} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Ikona z werdyktem AI do listy faktur PRZ — renderuje się tylko wtedy, gdy backend
 * dostarczył analizę dla tego oglądającego.
 */
export function ClientInvoiceAiBadge({ analysis }: { analysis?: ClientInvoiceAiAnalysis | null }) {
  if (!analysis) return null;
  const { Icon, cls, spin } = STYLES[analysis.status] ?? STYLES.warning;
  return (
    <span
      className="inline-flex items-center gap-0.5 align-middle"
      title={analysis.summary || analysis.error_message || analysis.status_label}
      aria-label="Analiza AI faktury"
    >
      <Sparkles className="w-3 h-3 text-purple-500" />
      <Icon className={`w-4 h-4 ${cls} ${spin ? "animate-spin" : ""}`} />
    </span>
  );
}
